//changed due to package upgrade
import React from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { alpha, Box, Button, TextField, Typography } from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import Grid from '@mui/material/Grid';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { Fonts } from '@projectK/constants/AppEnums';
import AppInfoView from '@projectK/components/AppInfoView';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppCard from '@projectK/components/AppCard';

const validationSchema = yup.object({
  emergency_contact_name: yup.string().nullable().optional(),
  emergency_contact_number: yup
    .string()
    .nullable()
    .optional()
    .matches(
      /^(\+\d{1,2}\s?)?(\()?\d{3}(\))?[-.\s]?\d{3}[-.\s]?\d{4}$/,
      'Please enter a valid phone number.',
    ),
  relation: yup.string().nullable().optional(),
});

const EmergencyInfo = ({ fetchData, settings, onClose }) => {
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const { user } = useAuthUser();
  return (
    <AppCard>
      <Box>
        <Formik
          enableReinitialize={true}
          validateOnBlur={true}
          initialValues={{
            emergency_contact_name: settings?.emergency_contact_name || '',
            emergency_contact_number: settings?.emergency_contact_number || '',
            relation: settings?.relation || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            const requestData = {
              emergency_contact_name:
                values.emergency_contact_name === ''
                  ? null
                  : values.emergency_contact_name,
              emergency_contact_number:
                values.emergency_contact_number === ''
                  ? null
                  : values.emergency_contact_number,
              relation: values.relation === '' ? null : values.relation,
            };
            jwtAxios
              .put(`/users/${user.id}`, requestData)
              .then((response) => {
                setSubmitting(false);
                fetchData();
                infoViewActionsContext.showMessage(
                  'Emergency Information Updated Successfully',
                );
              })
              .catch((error) => {
                setSubmitting(false);
                infoViewActionsContext.fetchError(error.response.data.error);
              });
          }}
        >
          {({
            values,
            setFieldValue,
            handleChange,
            resetForm,
            touched,
            errors,
          }) => {
            return (
              <Form noValidate autoComplete='off'>
                <Typography
                  component='h3'
                  sx={{
                    fontSize: 16,
                    fontWeight: Fonts.BOLD,
                    color: companyTheme
                      ? companyTheme.primary.main
                      : 'text.primary',
                  }}
                >
                  <IntlMessages id='common.emergencyInfo' />
                </Typography>
                <Grid size={{ xs: 12, md: 6 }}>
                  <AppGridContainer sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='emergency_contact_name'
                        value={values.emergency_contact_name}
                        onChange={(event) => {
                          setFieldValue(
                            'emergency_contact_name',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.emergency_contact_name = true;
                        }}
                        error={
                          touched.emergency_contact_name &&
                          Boolean(errors.emergency_contact_name)
                        }
                        helperText={
                          touched.emergency_contact_name &&
                          errors.emergency_contact_name
                        }
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Emergency Contact Name'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='emergency_contact_number'
                        value={values.emergency_contact_number}
                        onChange={(event) => {
                          setFieldValue(
                            'emergency_contact_number',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.emergency_contact_number = true;
                        }}
                        error={
                          touched.emergency_contact_number &&
                          Boolean(errors.emergency_contact_number)
                        }
                        helperText={
                          touched.emergency_contact_number &&
                          errors.emergency_contact_number
                        }
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Emergency Contact Number'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='relation'
                        value={values.relation}
                        onChange={(event) => {
                          setFieldValue(
                            'relation',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.relation = true;
                        }}
                        error={touched.relation && Boolean(errors.relation)}
                        helperText={touched.relation && errors.relation}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Relation To Member '
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            backgroundColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              backgroundColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                : alpha('#2196f3', 0.8),
                            },
                          }}
                          variant='contained'
                          type='submit'
                        >
                          Save
                        </Button>

                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            ml: 2.5,
                            borderColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              borderColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8)
                                : alpha('#2196f3', 0.8),
                            },
                            color: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                          }}
                          variant='outlined'
                          onClick={(e) => {
                            e.preventDefault();
                            // Reset the form back to its initial state
                            resetForm();
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Grid>
                  </AppGridContainer>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Box>
      <AppInfoView />
    </AppCard>
  );
};

export default EmergencyInfo;

EmergencyInfo.propTypes = {
  fetchData: PropTypes.func,
  settings: PropTypes.object,
  onClose: PropTypes.func,
};
